import React, {useState} from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import {styled} from '@mui/material/styles';
import {Chip, Typography, Checkbox, Button, Dialog, DialogTitle, DialogContent, DialogActions} from '@mui/material';
import { formatDateToJerusalem } from '../../../../../utils/dateFormatter';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
  padding: '16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:first-of-type': {
    paddingLeft: 24,
  },
  '&:last-of-type': {
    paddingRight: 24,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.grey[50],
  },
}));

const TableItem = ({data, index, selected, onSelectRow}) => {
  const formatDate = (dateString) => {
    return formatDateToJerusalem(dateString);
  };

  const getSourceColor = (source) => {
    if (!source) return 'default';
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('api')) return 'info';
    if (lowerSource.includes('file') || lowerSource.includes('upload')) return 'secondary';
    if (lowerSource.includes('manual')) return 'success';
    return 'default';
  };

  const formatOtherData = (otherData) => {
    if (!otherData) return 'N/A';
    try {
      if (typeof otherData === 'string') {
        const parsed = JSON.parse(otherData);
        return typeof parsed === 'object' ? JSON.stringify(parsed) : parsed;
      }
      return typeof otherData === 'object' ? JSON.stringify(otherData) : otherData;
    } catch {
      return String(otherData);
    }
  };

  return (
    <StyledTableRow key={data.id} index={index} selected={selected}>
      {/* Checkbox */}
      <StyledTableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={() => onSelectRow(data.id)}
          color="primary"
        />
      </StyledTableCell>

      {/* ID */}
      <StyledTableCell>
        <Chip 
          label={data.id} 
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, minWidth: 60 }}
        />
      </StyledTableCell>

      {/* Full Name */}
      <StyledTableCell>
        <Typography 
          variant="body2" 
          fontWeight={600}
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200
          }}
          title={data.fullName || data.name || 'N/A'}
        >
          {data.fullName || data.name || 'N/A'}
        </Typography>
      </StyledTableCell>

      {/* Age */}
      <StyledTableCell>
        <Typography variant="body2" fontWeight={500}>
          {data.age || 'N/A'}
        </Typography>
      </StyledTableCell>

      {/* Email */}
      <StyledTableCell>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 250
          }}
          title={data.email || 'N/A'}
        >
          {data.email || 'N/A'}
        </Typography>
      </StyledTableCell>

      {/* Country */}
      <StyledTableCell>
        <Chip 
          label={data.country || 'N/A'} 
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </StyledTableCell>

      {/* Marks */}
      <StyledTableCell>
        <ShowMarks marks={data.marks} />
      </StyledTableCell>

      {/* MPC_4 */}
      <StyledTableCell>
        <Chip 
          label={data.MPC_4 || 'N/A'} 
          color="primary"
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </StyledTableCell>

      {/* Data Source */}
      <StyledTableCell>
        <Chip 
          label={data.data_source || 'Manual'} 
          color={getSourceColor(data.data_source)}
          size="small"
        />
      </StyledTableCell>

      {/* Other Data */}
      <StyledTableCell>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 240
          }}
          title={formatOtherData(data.other_data)}
        >
          {formatOtherData(data.other_data)}
        </Typography>
      </StyledTableCell>

      {/* Integration Source */}
      <StyledTableCell>
        <Chip 
          label={data.integration_source || 'Manual'} 
          color="primary"
          size="small"
          variant="outlined"
        />
      </StyledTableCell>

      {/* Sent At */}
      <StyledTableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(data.sent_at)}
        </Typography>
      </StyledTableCell>

      {/* Created At */}
      <StyledTableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(data.created_at)}
        </Typography>
      </StyledTableCell>

      {/* Updated At */}
      <StyledTableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(data.updated_at)}
        </Typography>
      </StyledTableCell>
    </StyledTableRow>
  );
};

// Inline component to show marks in a modal
const ShowMarks = ({marks}) => {
  const [open, setOpen] = useState(false);
  const pretty = (() => {
    if (!marks) return 'N/A';
    try {
      const obj = typeof marks === 'string' ? JSON.parse(marks) : marks;
      return typeof obj === 'object' ? JSON.stringify(obj, null, 2) : String(obj);
    } catch {
      return String(marks);
    }
  })();
  return (
    <>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)}>Show</Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Marks</DialogTitle>
        <DialogContent>
          <Typography component="pre" sx={{whiteSpace: 'pre-wrap', fontFamily: 'monospace'}}>
            {pretty}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ShowMarks.propTypes = { marks: PropTypes.any };

export default TableItem;

TableItem.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number,
  selected: PropTypes.bool.isRequired,
  onSelectRow: PropTypes.func.isRequired,
};
